<template>
  <div class="active-order-card" v-if="!isNpsFiredForOrder">
    <observer
      :keepConnectedOnIntersection="true"
      :emitNotIntersecting="true"
      :options="{
        root: null,
        threshold: 0.5,
        rootMargin: '0px -16px 0px -16px',
      }"
      @intersect="cardObserved"
      @notIntersecting="cardUnobserved"
    />
    <div
      v-if="isCurrentOrder"
      class="flex-row justify-content-between width100 plr12 pt12"
      :class="[postDeliveryBgColorClass]"
    >
      <div class="flex-column align-items-start tl pb12">
        <div class="active-order-card-time mb12">
          <span
            v-if="showBoltIcon"
            class="fs-11 fw-800 active-order-card-on-time"
            :class="[timeStatusTagBgColorClass, timeStatusTagFontColorClass]"
            ><i class="hkfi-bolt-icon fs-12 mr3" />{{ timeStatusValue }}</span
          >
          <div class="active-order-card-desc fs-16 fw-700">
            {{ timeLineText }}
          </div>
        </div>

        <div class="active-order-card-desc color-slaty fs-14 fw-400">
          {{ digInText }}
        </div>
      </div>
      <div class="flex-row">
        <div class="delivered-img-container">
          <img :src="getOrderStatusImgUrl" class="delivered-img" />
        </div>
        <i
          class="hkfi-expand fs-24 color-slaty"
          @click="sendtoOrderHistoryPage"
        />
      </div>
    </div>
    <div
      v-if="isCurrentOrder"
      class="inline-flex align-items-center justify-content-between w100 plr16 pb12 pt12 gap8"
    >
      <div class="tl">{{ bottomText }}</div>
      <button
        class="btn bold primary order-cta-btn fs-12 fw-800"
        v-if="primaryCTAText"
        @click="markItDelivered"
      >
        {{ primaryCTAText }}
      </button>
      <a :href="'tel:' + customerSupportMobileNumber">
        <button
          class="btn bold primary order-cta-btn fs-12 fw-800"
          v-if="secondaryCTAText"
        >
          {{ secondaryCTAText }}
        </button>
      </a>
    </div>
    <div class="p16" v-if="!isCurrentOrder">
      <active-order-card-shimmer />
    </div>
  </div>
</template>

<script>
import get from "lodash/get";
import { keyMapsAction, keyMapsMutation } from "../helpers/storeKeys";
import { clientPlatform, staticImgBaseUrl } from "../config/apiEndpoints";
import ProgressBar from "./reusableComponents/ProgressBar.vue";
import Observer from "./Observer.vue";
import ActiveOrderCardShimmer from "./ActiveOrderCardShimmer.vue";
import Utility from "../helpers/utility";
import LocalStorageFunc from "../helpers/localStorageFunc";
import { OrderHistoryPages } from "../helpers/orderHistoryConstants";
import {
  OrderStatusTimelineTextForDelivery,
  OrderStatusHelpBottomTextForDeliveryKey,
  OrderStatusImgIconKey,
  TimeStatusTagBgColorClass,
  TimeStatusTagFontColorClass,
  ShowBoltIcon,
  PostDeliveryBgColorClass,
  DigInText,
} from "../helpers/OrderStatusConstants";

export default {
  name: "PostDeliveryOrderCard",
  components: {
    ProgressBar,
    Observer,
    ActiveOrderCardShimmer,
  },
  props: {
    dataResults: { required: false, default: "" },
    sourcePage: { required: false, default: "" },
    activateOrderCardClick: { required: false, default: false },
    index: { required: false, default: 0 },
    dataProps: { required: true, default: {} },
  },
  data() {
    return {
      staticImgBaseUrl,
      clientPlatform,
      animationInstance: {},
      modulePropsData: {},
      inView: false,
      order: {},
      limit: 3,
      ratingTags: {},
      moduleProps: {},
      startPolling: false,
    };
  },
  mounted() {
    this.order = this.dataResults;
    this.moduleProps = this.dataProps;
  },
  methods: {
    cardObserved() {
      this.startPolling = true;
      setTimeout(() => {
        this.$store.dispatch(keyMapsAction.POST_ORDER_TRACKER_API, {
          orderId: this.order?.id,
        });
        this.pollData();
      }, 60);
    },
    cardUnobserved() {
      this.startPolling = false;
    },
    pollData() {
      if (this.startPolling) {
        setTimeout(() => {
          if (this.isCurrentOrder && !this.isOrderDelivered) {
            this.$store.dispatch(keyMapsAction.POST_ORDER_TRACKER_API, {
              orderId: this.order?.id,
            });
            this.pollData();
          }
        }, 60000);
      }
    },
    markItDelivered() {
      this.$store.commit(
        keyMapsMutation.ADD_DELIVERED_ORDER_IDS,
        this.order?.id,
      );
      this.$store.commit(
        keyMapsMutation.ADD_NPS_FIRED_ORDER_IDS,
        this.order?.id,
      );
      this.pollNpsCsatHomeWidget();
    },
    pollNpsCsatHomeWidget() {
      setTimeout(() => {
        if (this.$store.state.CsatFeedback.showNpsCsatFeedback) return;
        let userData = LocalStorageFunc.getUserData()?.userData;
        if (userData && typeof userData === "string") {
          userData = Utility.decryptData(userData);
        }
        this.$store.dispatch(keyMapsAction.GET_NPS_CSAT_HOME_WIDGET, {
          mobile: userData?.mobile || "",
        });
        this.pollNpsCsatHomeWidget();
      }, 10000);
    },
    sendToTrackingPage() {
      LocalStorageFunc.setOrderSummary(this.order);
      Utility.sendToOrderTracking();
    },
    sendtoOrderHistoryPage() {
      LocalStorageFunc.setOrderSummary(this.order);

      LocalStorageFunc.setOrderHistoryPage(
        OrderHistoryPages.PREVIOUS_ORDERS_DETAIL,
      );
      Utility.sendToOrderHistory();
    },
  },
  computed: {
    currentOrderState() {
      return this.isCurrentOrder && this.postOrderTrackerResponse?.orderStatus
        ? this.postOrderTrackerResponse?.orderStatusCode
        : this.order?.orderCurrentState;
    },
    progressBarPercent() {
      return this.isCurrentOrder && this.postOrderTrackerResponse?.orderStatus
        ? this.eta.progress
        : this.currentOrderState * 10;
    },
    timeLineText() {
      return this.isCurrentOrder && this.postOrderTrackerResponse?.orderStatus
        ? this.orderTrackingMessages?.statusMessage ||
            this.orderTrackingMessages?.etaMessage
        : OrderStatusTimelineTextForDelivery[this.currentOrderState];
    },
    bottomText() {
      return get(
        this.moduleProps,
        OrderStatusHelpBottomTextForDeliveryKey[this.currentOrderState],
        "",
      );
    },
    deliveredSubTxt() {
      return this.moduleProps?.deliveredSubTxt;
    },
    postOrderTrackerResponse() {
      return this.$store.state.OrderTracking?.postOrderTrackerResponse;
    },
    eta() {
      return this.postOrderTrackerResponse?.etaWidget?.eta;
    },
    etaTime() {
      return this.eta?.eta;
    },
    timeStatusValue() {
      return this.eta?.timeStatusValue;
    },
    orderTrackingMessages() {
      return this.postOrderTrackerResponse?.etaWidget?.orderTrackingMessages;
    },
    isCurrentOrder() {
      return (
        this.order.id == this.$store.state.OrderTracking?.currentTrackerOrderId
      );
    },
    isOrderDelivered() {
      return this.currentOrderState == 9;
    },
    isNpsFiredForOrder() {
      return this.$store.state.OrderTracking.npsFiredOrderIds.includes(
        this.order?.id,
      );
    },
    getOrderStatusImgUrl() {
      return get(
        this.moduleProps,
        OrderStatusImgIconKey[this.currentOrderState],
        "",
      );
    },
    primaryCTAText() {
      return this.moduleProps?.yes_btn_txt || "Yes, Got It!";
    },
    secondaryCTAText() {
      return this.moduleProps?.no_btn_txt || "No, Call Rider";
    },
    customerSupportMobileNumber() {
      return this.moduleProps?.customerSupportMobileNumber || "18003091234";
    },
    timeStatusTagBgColorClass() {
      return TimeStatusTagBgColorClass[this.eta?.timeStatus];
    },
    timeStatusTagFontColorClass() {
      return TimeStatusTagFontColorClass[this.eta?.timeStatus];
    },
    showBoltIcon() {
      return ShowBoltIcon[this.eta?.timeStatus];
    },
    postDeliveryBgColorClass() {
      return PostDeliveryBgColorClass[this.eta?.timeStatus];
    },
    digInText() {
      return this.moduleProps?.digInText || DigInText;
    },
  },
};
</script>
